import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UiState {
  // Desktop: left-nav collapsed to nothing (toggle in TopBar / nav header).
  navCollapsed: boolean;
  setNavCollapsed: (v: boolean) => void;
  toggleNav: () => void;
  // Mobile: off-canvas drawer open.
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
  // Focus mode — every piece of app chrome hidden at once, so the lesson
  // itself fills the screen. Three separate toggles already existed (left nav,
  // the lecture's slide rail, the stage's own fullscreen) and clearing the
  // screen meant finding all three. This is the one control that does it.
  //
  // Deliberately NOT persisted: focus mode hides the navigation, so a session
  // that restored it would drop the user into a chrome-less screen they did
  // not ask for and, on a narrow window, could not obviously escape. It is a
  // per-visit mode, and Escape always leaves it.
  focusMode: boolean;
  setFocusMode: (v: boolean) => void;
  toggleFocusMode: () => void;
  // Week tree default: the LAST state the "Collapse all / Expand all weeks"
  // button in LeftNav's panel header was left in. Applied as the starting
  // point when a course's weeks load; a learner can still open/close one
  // week at a time without changing this preference.
  //
  // Persisted, unlike focusMode: this is a chrome PREFERENCE ("I like the
  // tree collapsed by default"), not a mode that hides the way back out —
  // the sidebar itself, and every week header, stay visible and clickable
  // either way. Same reasoning as navCollapsed.
  weekTreeCollapsed: boolean;
  setWeekTreeCollapsed: (v: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      navCollapsed: false,
      setNavCollapsed: (v) => set({ navCollapsed: v }),
      toggleNav: () => set({ navCollapsed: !get().navCollapsed }),
      mobileNavOpen: false,
      setMobileNavOpen: (v) => set({ mobileNavOpen: v }),
      focusMode: false,
      // Entering focus mode closes the mobile drawer too: leaving it open would
      // put an off-canvas nav on top of the very screen focus mode just cleared.
      setFocusMode: (v) => set(v ? { focusMode: true, mobileNavOpen: false } : { focusMode: false }),
      toggleFocusMode: () =>
        set(get().focusMode ? { focusMode: false } : { focusMode: true, mobileNavOpen: false }),
      weekTreeCollapsed: false,
      setWeekTreeCollapsed: (v) => set({ weekTreeCollapsed: v }),
    }),
    {
      name: 'acechem-ui',
      partialize: (s) => ({ navCollapsed: s.navCollapsed, weekTreeCollapsed: s.weekTreeCollapsed }),
    }
  )
);
